import noop from 'lodash/noop';
import { useTranslation } from 'next-i18next';
import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Button } from '@hotelplan/components.common.buttons';
import { setTooltipPosition } from '@hotelplan/components.common.tooltip';
import { FdrLazyTooltip } from 'fdr/components/local/fdr-lazy-tooltip';

interface IEditableContainer extends React.PropsWithChildren<{}> {
  tooltipText?: string;
  onEdit?(e?: React.MouseEvent<HTMLElement>): void;
  className?: string;
}

const EditableContainerWrap = styled.div({
  position: 'relative',
});

const EditButton = styled(Button)({
  zIndex: 4,
  position: 'absolute',
  ':hover': {
    '.tooltip, .tooltipArrow': {
      visibility: 'visible',
    },
  },
});

const ICON_WIDTH_MD = 28;
const ICON_HALF_WIDTH_MD = 10;

const EditableContainer: React.FC<IEditableContainer> = ({
  tooltipText,
  onEdit = noop,
  className,
  children,
}) => {
  const [t] = useTranslation('common');
  const tooltipElRef = useRef<HTMLElement>(null);

  useEffect(
    function adjustTooltipPosition() {
      if (!tooltipElRef.current) return;

      setTooltipPosition(tooltipElRef.current as HTMLElement, {
        tooltipTargetObjectWidth: ICON_WIDTH_MD,
        tooltipTargetObjectHalfWidth: ICON_HALF_WIDTH_MD,
      });
    },
    [tooltipElRef]
  );

  return (
    <EditableContainerWrap
      className={`editable-container ${className}`}
      data-id="editable-container"
    >
      <EditButton
        className="edit-button"
        onClick={onEdit}
        variant="iconBtn"
        data-id="edit-btn-id"
        icon={{
          name: 'edit',
          size: 'md',
        }}
      >
        {t('common:edit')}
        {tooltipText && (
          <FdrLazyTooltip
            withAutoPositioning
            tooltipElRef={tooltipElRef}
            testId="edit-icon-tooltip"
          >
            {tooltipText}
          </FdrLazyTooltip>
        )}
      </EditButton>
      {children}
    </EditableContainerWrap>
  );
};

export default EditableContainer;
